import {FormControlLabel, Checkbox, Divider, Typography, Stack, Alert} from '@mui/material';

import {StyledAdornedEnd} from 'styles/InputMenu';
import InputActionButtons from './InputActionButtons';
import {handleKeyDown} from 'components/InputMenu/helpers';

import useAddEditHarvestDemand from 'hooks/addEdit/useAddEditHarvestDemand';
import {useInputMenuContext} from 'context/InputMenu';

import NumberFormatCustom from 'helpers/NumberFormatter';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import GenericTextField from 'components/Generic/TextField';
import {useTranslation} from 'react-i18next';

import {SpeciesSelector} from './SpeciesSelector';
import {CertificatesSelector} from './CertificatesSelector';
import {type HarvestDemandPutCertificatesEnum, type HarvestDemandPutSpeciesEnum} from 'client';
import {WeightUnitSelector} from 'components/Shared/WeightUnitSelector';
import {useFilterContext} from 'context/FilterContext';
import {DEFAULT_WEIGHT_UNIT} from 'appConstants';
import {useGetHogLweFormatter} from 'hooks/useGetHogLweFormatter';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {InputMenuDatePicker} from 'components/Generic/DatePickersV2/InputMenuDatePicker';

function HarvestDemand() {
  const {t} = useTranslation('common');
  const {multipleComplexHarvestDemandEnabled} = useFlags();
  const filterContext = useFilterContext();
  const {formatLweHog, formatToLwe} = useGetHogLweFormatter();

  const {harvestDemand, setHarvestDemand, setCreateUpdateHarvestDemand, createUpdateHarvestDemand} =
    useAddEditHarvestDemand();

  const inputMenuDispatch = useInputMenuContext()?.dispatch;

  return (
    <form noValidate autoComplete="off" style={{width: '100%'}}>
      <Stack spacing={2} px={0.5} py={1}>
        <GenericTextField
          label={t('name')}
          value={harvestDemand.name}
          onChange={(event) => {
            const name = event.target.value;
            setHarvestDemand({...harvestDemand, name});
          }}
          aria-label={t('name')}
          onKeyDown={(event) => {
            inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateHarvestDemand);
          }}
          data-testid="name"
        />

        {/* Start Week */}
        <InputMenuDatePicker
          value={harvestDemand.startWeek}
          onChange={(date: Date | null) => {
            date && setHarvestDemand({...harvestDemand, startWeek: date});
          }}
          label={`${t('week')} / ${t('year')}`}
        />

        <Stack direction="row" justifyContent="flex-end" flexGrow={1}>
          <WeightUnitSelector />
        </Stack>

        {/* Volume per week */}
        <GenericTextField
          inputProps={{'data-testid': 'weight'}}
          InputProps={{
            inputComponent: NumberFormatCustom as (props) => JSX.Element,
            endAdornment: (
              <StyledAdornedEnd>{`${t(filterContext.weightUnit ?? DEFAULT_WEIGHT_UNIT)} ${t('ton')}`}</StyledAdornedEnd>
            ),
          }}
          label={t('weeklyDemand')}
          value={
            harvestDemand.volumePerWeek > 0
              ? Math.round(formatLweHog(harvestDemand.volumePerWeek, harvestDemand.species) * 100) / 100
              : ''
          }
          onChange={(event) => {
            const volumePerWeek = formatToLwe(parseFloat(event.target.value), harvestDemand.species);
            setHarvestDemand({...harvestDemand, volumePerWeek});
          }}
          onKeyDown={(event) =>
            inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreateUpdateHarvestDemand)
          }
        />
        <Stack spacing={0}>
          <FormControlLabel
            control={
              <Checkbox
                checked={harvestDemand.repeatEveryWeek}
                onChange={() => {
                  setHarvestDemand({
                    ...harvestDemand,
                    repeatEveryWeek: !harvestDemand.repeatEveryWeek,
                    ...(harvestDemand.repeatEveryWeek && {multiHarvests: false}),
                  });
                }}
              />
            }
            label={t('repeatEveryWeek')}
            data-testid="repeat"
          />
          {harvestDemand.repeatEveryWeek && (
            <Stack spacing={0}>
              <Stack>
                <Typography>{t('ends')}</Typography>
              </Stack>
              <Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={harvestDemand.endWeek === undefined && harvestDemand.repeatEveryWeek}
                      onChange={() => {
                        if (harvestDemand.endWeek) {
                          setHarvestDemand({...harvestDemand, endWeek: undefined});
                        }
                      }}
                      checkedIcon={<RadioButtonCheckedIcon />}
                      icon={<RadioButtonUncheckedIcon />}
                      size="small"
                    />
                  }
                  label={t('never')}
                />
              </Stack>
              <Stack direction={'column'} spacing={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={harvestDemand.endWeek !== undefined && harvestDemand.repeatEveryWeek}
                      onChange={() => {
                        setHarvestDemand({
                          ...harvestDemand,
                          endWeek: harvestDemand.startWeek ? harvestDemand.startWeek : new Date(),
                        });
                      }}
                      checkedIcon={<RadioButtonCheckedIcon />}
                      icon={<RadioButtonUncheckedIcon />}
                      size="small"
                    />
                  }
                  label={t('on')}
                />

                {/* End Week */}
                <InputMenuDatePicker
                  value={harvestDemand.endWeek}
                  onChange={(date: Date | null) => {
                    setHarvestDemand({...harvestDemand, endWeek: date});
                  }}
                  label={`${t('week')} / ${t('year')}`}
                />
              </Stack>
              <Stack py={2}>
                <Divider />
              </Stack>
            </Stack>
          )}
        </Stack>

        {/* Fish Species */}
        <SpeciesSelector
          species={harvestDemand.species}
          setSpecies={(species?: HarvestDemandPutSpeciesEnum) => {
            species && setHarvestDemand({...harvestDemand, species});
          }}
        />

        {/* Fish Certificates */}
        <CertificatesSelector
          certificates={harvestDemand.certificates ?? []}
          setCertificates={(certificates: HarvestDemandPutCertificatesEnum[]) => {
            setHarvestDemand({...harvestDemand, certificates});
          }}
        />

        {/* Multiple complex harvest demands */}
        {multipleComplexHarvestDemandEnabled && harvestDemand.repeatEveryWeek && (
          <Stack>
            <Stack py={1}>
              <Divider />
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={harvestDemand.multiHarvests}
                  onChange={() => {
                    setHarvestDemand({...harvestDemand, multiHarvests: !harvestDemand.multiHarvests});
                  }}
                />
              }
              label={t('complex')}
              data-testid="complex"
            />
            <Stack py={1}>
              <Alert variant="outlined" severity="warning">
                {t('complexExplanation')}
              </Alert>
            </Stack>
          </Stack>
        )}

        <InputActionButtons setCreateUpdateInput={setCreateUpdateHarvestDemand} disabled={createUpdateHarvestDemand} />
      </Stack>
    </form>
  );
}

export default HarvestDemand;
